import React, { useState } from 'react';
import moment from 'moment';
import Drawer from '@mui/material/Drawer';
import { Button, IconButton, Skeleton } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { RTL } from 'arbitrip-common/translation/const';

import { ReactComponent as CashWalletFileIcon } from '@/img/icons/cash-wallet-file-icon.svg';
import { ReactComponent as CashWalletRejectionIcon } from '@/img/icons/cash-wallet-rejection-icon.svg';

import Ajax from '../../../utils/Ajax';
import CashWalletConstants from './CashWalletConstants';
import { formatMoney } from './CashWalletMoney';
import StatusIndicator from './StatusIndicator.react';
import CancelConfirmDialog from './CancelConfirmDialog.react';

const { REQUEST_STATUS, STATUS, TAX_CLASSIFICATION, DATE_FORMAT, DEFAULT_CURRENCY } = CashWalletConstants;

function formatDate(value) {
    return value ? moment(value).format(DATE_FORMAT) : '';
}

const DetailTopCard = ({ detail, currency }) => {
    const { t } = useTranslation('CashWallet');
    const price = formatMoney(detail.amount, currency);

    return (
        <div className="cash-wallet-detail-top-card">
            <div className="cash-wallet-detail-top-head">
                <span className="cash-wallet-detail-card-title">{t('Detail.CardTitle')}</span>
                <StatusIndicator status={detail.status} />
            </div>
            <div className="cash-wallet-detail-amount">{price}</div>
        </div>
    );
};

const RejectionBanner = ({ detail }) => {
    const { t } = useTranslation('CashWallet');

    if (detail.status !== REQUEST_STATUS.REJECTED) {
        return null;
    }

    const reason = t(`Rejection.Reason.${detail.rejection_reason}`, {
        comment: detail.rejection_comment || '',
    });

    return (
        <div className="cash-wallet-detail-rejection-banner">
            <span className="cash-wallet-detail-rejection-icon">
                <CashWalletRejectionIcon className="cash-wallet-detail-rejection-icon-glyph" />
            </span>
            <span className="cash-wallet-detail-rejection-message">{t('Rejection.Banner', { reason })}</span>
        </div>
    );
};

const DetailInfoRow = ({ label, value }) => (
    <div className="cash-wallet-detail-info-row">
        <span className="cash-wallet-detail-info-label">{label}</span>
        <span className="cash-wallet-detail-info-value">{value}</span>
    </div>
);

const DetailInfoCard = ({ detail }) => {
    const { t } = useTranslation('CashWallet');

    let thirdRow = null;
    switch (detail.status) {
        case REQUEST_STATUS.SUBMITTED:
            thirdRow = <DetailInfoRow label={t('Detail.ExpectedBy')} value={formatDate(detail.expected_payout_by)} />;
            break;
        case REQUEST_STATUS.PAID:
            thirdRow = <DetailInfoRow label={t('Detail.PaidOn')} value={formatDate(detail.resolved_at)} />;
            break;
        case REQUEST_STATUS.REJECTED:
            thirdRow = <DetailInfoRow label={t('Detail.RejectedOn')} value={formatDate(detail.resolved_at)} />;
            break;
        case REQUEST_STATUS.CANCELLED:
            thirdRow = <DetailInfoRow label={t('Detail.CancelledOn')} value={formatDate(detail.resolved_at)} />;
            break;
        default:
            thirdRow = null;
    }

    return (
        <div className="cash-wallet-detail-info-card">
            <div className="cash-wallet-detail-card-heading">{t('Detail.DetailsTitle')}</div>
            <div className="cash-wallet-detail-info-rows">
                <DetailInfoRow label={t('Detail.PaymentRequestId')} value={detail.short_id} />
                <DetailInfoRow label={t('Detail.Submitted')} value={formatDate(detail.submitted_at)} />
                {thirdRow}
            </div>
        </div>
    );
};

const getTaxClassificationLabel = (t, taxClassification) => {
    switch (taxClassification) {
        case TAX_CLASSIFICATION.AUTHORISED:
            return t('Drawer.TaxAuthorised');
        case TAX_CLASSIFICATION.EXEMPT:
            return t('Drawer.TaxExempt');
        default:
            return taxClassification;
    }
};

const BreakdownCard = ({ detail, currency }) => {
    const { t } = useTranslation('CashWallet');
    const isExempt = detail.tax_classification === TAX_CLASSIFICATION.EXEMPT;

    return (
        <div className="cash-wallet-detail-card">
            <div className="cash-wallet-detail-card-heading">{t('Detail.BreakdownTitle')}</div>
            <DetailInfoRow
                label={t('Detail.TaxClassification')}
                value={getTaxClassificationLabel(t, detail.tax_classification)}
            />
            <DetailInfoRow label={t('Detail.NetAmount')} value={formatMoney(detail.net, currency)} />
            <DetailInfoRow
                label={t('Detail.Vat')}
                value={isExempt ? t('Drawer.NoVat') : formatMoney(detail.vat, currency)}
            />
            <div className="cash-wallet-detail-breakdown-divider" />
            <DetailInfoRow label={t('Detail.Total')} value={formatMoney(detail.amount, currency)} />
        </div>
    );
};

const BillToCard = ({ billTo }) => {
    const { t } = useTranslation('CashWallet');

    if (!billTo) {
        return null;
    }

    return (
        <div className="cash-wallet-detail-card">
            <div className="cash-wallet-detail-card-heading">{t('Detail.BillToTitle')}</div>
            <div className="cash-wallet-detail-bill-to">
                <div>{billTo.name}</div>
                <div>{billTo.phone}</div>
                <div>{billTo.email}</div>
                <div>{billTo.address}</div>
            </div>
        </div>
    );
};

const InvoiceLink = ({ detail }) => {
    const { t } = useTranslation('CashWallet');

    if (!detail.invoice_download_url) {
        return null;
    }

    const handleClick = () => {
        Ajax.getCashWalletInvoiceUrl(detail.id)
            .done((response) => {
                const url = response && response.url;
                if (url) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                }
            })
            .fail((error) => {
                console.error('Error while fetching cash wallet invoice url: ', error);
            });
    };

    return (
        <div className="cash-wallet-detail-invoice-section">
            <div className="cash-wallet-detail-invoice-heading">{t('Detail.InvoiceTitle')}</div>
            <button type="button" className="cash-wallet-invoice-chip" onClick={handleClick}>
                <CashWalletFileIcon className="cash-wallet-invoice-chip-icon" />
                <span>{detail.invoice_filename || detail.short_id}</span>
            </button>
        </div>
    );
};

const DetailDrawerFooter = ({ detail, onCancelClick }) => {
    const { t } = useTranslation('CashWallet');

    if (detail.status !== REQUEST_STATUS.SUBMITTED) {
        return null;
    }

    return (
        <div className="cash-wallet-detail-footer">
            <Button variant="outlined" color="danger" fullWidth onClick={onCancelClick}>
                {t('Detail.CancelRequest')}
            </Button>
        </div>
    );
};

const DetailSkeleton = () => (
    <div className="cash-wallet-detail-skeleton">
        <Skeleton variant="rounded" height={96} />
        <Skeleton variant="rounded" height={120} />
        <Skeleton variant="rounded" height={160} />
    </div>
);

const DetailDrawer = ({ open, onClose, detail, detailStatus, billTo, currency, onConfirmCancel }) => {
    const { t, i18n } = useTranslation('CashWallet');
    const [confirmOpen, setConfirmOpen] = useState(false);

    const isRTL = i18n.dir() === RTL;
    const loading = detailStatus === STATUS.INITIAL || detailStatus === STATUS.BUSY;
    const resolvedCurrency = currency || detail?.currency || DEFAULT_CURRENCY;

    const handleConfirm = () => {
        setConfirmOpen(false);
        if (detail) {
            onConfirmCancel(detail.id);
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            className="cash-wallet-detail-drawer"
            variant="temporary"
            SlideProps={{ appear: true }}
            sx={{
                '& .MuiDrawer-paper': {
                    direction: isRTL ? 'rtl' : 'ltr',
                },
            }}
        >
            <div className="cash-wallet-detail-inner">
                <div className="cash-wallet-detail-header">
                    <span className="cash-wallet-detail-title">{t('Detail.Title')}</span>
                    <IconButton className="cash-wallet-detail-close" aria-label="close" onClick={onClose}>
                        <CloseOutlined />
                    </IconButton>
                </div>

                {loading || !detail ? (
                    <DetailSkeleton />
                ) : (
                    <React.Fragment>
                        <div className="cash-wallet-detail-body">
                            <DetailTopCard detail={detail} currency={resolvedCurrency} />
                            <RejectionBanner detail={detail} />
                            <DetailInfoCard detail={detail} />
                            <BreakdownCard detail={detail} currency={resolvedCurrency} />
                            <BillToCard billTo={billTo} />
                            <InvoiceLink detail={detail} />
                        </div>
                        <DetailDrawerFooter detail={detail} onCancelClick={() => setConfirmOpen(true)} />
                    </React.Fragment>
                )}
            </div>

            <CancelConfirmDialog
                open={confirmOpen}
                onKeep={() => setConfirmOpen(false)}
                onConfirm={handleConfirm}
            />
        </Drawer>
    );
};

export default DetailDrawer;
